// History queries — ADR-0013 D3. The split between completed and
// unfinished workouts is the user's mental model: a finished workout is
// one with `completed_at` set, an unfinished one has sets logged but
// the user never tapped through to the end. Both rows exist on disk; the
// UI labels them differently and only the completed list contributes to
// the activity heatmap / streak. Tier derivation is independent — it
// comes from `aggregateProgress` over completed-only workouts.

import type { SyncedRow, WorkoutLogRow } from "./db";
import { getDb } from "./db";

export interface CompletedWorkout {
  id: string;
  date: string;
  totalReps: number;
  totalSeconds: number;
  exerciseIds: string[];
  startedAt: string;
  completedAt: string;
}

export interface UnfinishedWorkout {
  id: string;
  date: string;
  totalReps: number;
  totalSeconds: number;
  exerciseIds: string[];
  startedAt: string;
  setCount: number;
}

export interface WorkoutHistory {
  completed: CompletedWorkout[];
  unfinished: UnfinishedWorkout[];
}

/** Read every non-tombstoned workout for the owner, split by
 *  `completed_at`. Tombstoned rows (`deletedAt != null`) are excluded.
 *  Pure-ish: reads from Dexie. */
export async function loadWorkoutHistory(
  ownerId: string,
): Promise<WorkoutHistory> {
  if (!ownerId) return { completed: [], unfinished: [] };
  const db = getDb();
  const logs = await db.workout_logs
    .where("owner")
    .equals(ownerId)
    .and((r) => !r.deletedAt)
    .toArray();
  const entries = await db.log_entries
    .where("owner")
    .equals(ownerId)
    .and((r) => !r.deletedAt)
    .toArray();

  const entriesByWorkout = new Map<string, typeof entries>();
  for (const e of entries) {
    const bucket = entriesByWorkout.get(e.workout) ?? [];
    bucket.push(e);
    entriesByWorkout.set(e.workout, bucket);
  }

  const completed: CompletedWorkout[] = [];
  const unfinished: UnfinishedWorkout[] = [];

  for (const w of logs) {
    const e = entriesByWorkout.get(w.id) ?? [];
    const totalReps = e.reduce((s, x) => s + (x.reps ?? 0), 0);
    const totalSeconds = e.reduce((s, x) => s + (x.hold_seconds ?? 0), 0);
    const exerciseIds = Array.from(new Set(e.map((x) => x.exercise)));
    const date = (w.started_at ?? w.updatedAt).slice(0, 10);
    if (w.completed_at != null) {
      completed.push({
        id: w.id,
        date,
        totalReps,
        totalSeconds,
        exerciseIds,
        startedAt: w.started_at,
        completedAt: w.completed_at,
      });
    } else if (e.length > 0) {
      // Unfinished means at least one set was logged — a zero-set row
      // is either still in-flight or tombstoned-by-sweep; either way
      // it does not appear in history (ADR-0013 D3).
      unfinished.push({
        id: w.id,
        date,
        totalReps,
        totalSeconds,
        exerciseIds,
        startedAt: w.started_at,
        setCount: e.length,
      });
    }
  }

  // Keep legacy v1 workouts in the picture until they migrate. v1
  // rows live in the `workouts` table and were always treated as
  // completed (the v1 runner finished the workout when the user
  // tapped Done). Map them to the completed list.
  const legacy = (await db.workouts.toArray()).filter(
    (r) => (r as SyncedRow).owner === ownerId && !(r as SyncedRow).deletedAt,
  ) as SyncedRow[];
  for (const r of legacy) {
    const date = (r.updatedAt as string).slice(0, 10);
    const ex = (r as { exerciseId?: string }).exerciseId;
    const reps = Number((r as { reps?: number }).reps ?? 0);
    const seconds = Number((r as { hold_seconds?: number }).hold_seconds ?? 0);
    if (ex) {
      completed.push({
        id: (r as { id: string }).id,
        date,
        totalReps: reps,
        totalSeconds: seconds,
        exerciseIds: [ex],
        startedAt: r.updatedAt as string,
        completedAt: r.updatedAt as string,
      });
    }
  }

  // Newest first.
  completed.sort((a, b) => (a.startedAt < b.startedAt ? 1 : -1));
  unfinished.sort((a, b) => (a.startedAt < b.startedAt ? 1 : -1));

  return { completed, unfinished };
}

/** Count the sets logged for a given workout. Used by Resume to find the
 *  next `set_number`. */
export async function nextSetNumber(workoutId: string): Promise<number> {
  const db = getDb();
  const entries = await db.log_entries
    .where("workout")
    .equals(workoutId)
    .and((r) => !r.deletedAt)
    .toArray();
  const max = entries.reduce((m, e) => Math.max(m, e.set_number), 0);
  return max + 1;
}

/** Find the current exercise (the most recent set's exercise) for a
 *  workout — Resume navigates the runner back to it. */
export async function currentExerciseOfWorkout(
  workoutId: string,
): Promise<string | null> {
  const db = getDb();
  const entries = await db.log_entries
    .where("workout")
    .equals(workoutId)
    .and((r) => !r.deletedAt)
    .toArray();
  if (entries.length === 0) return null;
  entries.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  return entries[0]!.exercise;
}

/** Read a single workout row. */
export async function getWorkoutLog(
  workoutId: string,
): Promise<WorkoutLogRow | undefined> {
  const db = getDb();
  return db.workout_logs.get(workoutId);
}